import {
  createBinnedDataBufferFromData,
  type GeckoBinnedDataBuffer,
  useBinnedDataBufferCache,
  useCdfDataFetcher,
} from 'features/dataLayers'
import {
  serializeTminSource,
  type TminSource,
} from 'features/dataLayers/generation/calculations/calculateDerivedValues'
import { flatten2DValues } from 'features/dataLayers/generation/flatten2DValues'
import ShortTermRateWorker from 'features/dataLayers/generation/shortTermCorrosionRate/calculateShortTermRate.worker?worker'
import {
  PRIOR_SUPPORT_DISTANCE_WEIGHT,
  PRIOR_SUPPORT_MIN_COVERAGE_FRACTION,
  PRIOR_SUPPORT_MIN_VALUE_COUNT,
  PRIOR_SUPPORT_MISSING_COVERAGE_WEIGHT,
  PRIOR_SUPPORT_SEARCH_RADIUS_FEET,
  PRIOR_SUPPORT_TRANSLATION_STEP_BINS,
  TARGET_FEATURE_CORE_DFN_PERCENT,
  TARGET_FEATURE_GROW_DFN_PERCENT,
  TARGET_FEATURE_MAX_RECT_AREA_FEET_SQUARED,
  TARGET_FEATURE_MERGE_DISTANCE_FEET,
  TARGET_FEATURE_MIN_AREA_FEET_SQUARED,
  TARGET_FEATURE_RECT_PADDING_FEET,
} from 'features/dataLayers/generation/shortTermCorrosionRate/constants'
import {
  assembleGeneratedShortTermRateCdf,
  assembleGeneratedShortTermRemainingLifeCdf as assembleGeneratedShortTermRemainingLifeCdfBase,
  getGeneratedShortTermRateCdfId as getGeneratedShortTermRateCdfIdBase,
  getGeneratedShortTermRemainingLifeCdfId as getGeneratedShortTermRemainingLifeCdfIdBase,
  getShortTermTminSource,
} from 'features/dataLayers/generation/shortTermCorrosionRate/generatedShortTermCorrosionCdfs'
import {
  getFeatureGridMetadata,
  getTransferableGridValueBuffers,
} from 'features/dataLayers/generation/shortTermCorrosionRate/shortTermCorrosionWorkerClient'
import { type ShortTermRateGenerationSeries } from 'features/dataLayers/generation/shortTermCorrosionRate/shortTermRateGenerationSeries'
import type {
  CalculateShortTermFeatureRectangleRatesResult,
  CalculateShortTermFeatureRemainingLifeResult,
  DetectShortTermFeatureRectanglesResult,
  MatchShortTermFeatureRectanglesResult,
  ShortTermCorrosionFeatureRectangle,
  ShortTermCorrosionPriorSupport,
  ShortTermFeatureRateSummary,
  ShortTermRemainingLifeSummary,
} from 'features/dataLayers/generation/shortTermCorrosionRate/types'
import { runWebWorker } from 'features/webWorkerApi/runWebWorker'
import memoize from 'lodash/memoize'
import { useCallback } from 'react'
import {
  type ForecastComponentConfig,
  type GeckoAssetModel,
  GeckoComponentDataFrame,
  GeckoInspection,
  type GeckoMeasurement,
} from 'types'

export type ShortTermCorrosionRateGenerationParams = {
  target_feature_core_dfn_percent: number
  target_feature_grow_dfn_percent: number
  min_feature_area_feet_squared: number
  target_feature_merge_distance_feet: number
  target_feature_max_rect_area_feet_squared: number
  target_feature_rect_padding_feet: number
  prior_support_search_radius_feet: number
  prior_support_translation_step_bins: number
  prior_support_distance_weight: number
  prior_support_min_coverage_fraction: number
  prior_support_missing_coverage_weight: number
  prior_support_min_value_count: number
}

export type GenerateShortTermCorrosionRateCdfProps = {
  targetCdf: GeckoComponentDataFrame
  targetInspection: GeckoInspection
  comparisonCdf: GeckoComponentDataFrame
  comparisonInspection: GeckoInspection
  targetDifferenceFromNominalCdf: GeckoComponentDataFrame
  params?: ShortTermCorrosionRateGenerationParams
}

export type DetectShortTermCorrosionFeatureRectanglesProps = {
  targetCdf: GeckoComponentDataFrame
  targetDifferenceFromNominalCdf: GeckoComponentDataFrame
  observationRectangles?: readonly ShortTermCorrosionFeatureRectangle[]
  params?: ShortTermCorrosionRateGenerationParams
}

export type MatchShortTermCorrosionFeatureRectanglesProps = {
  targetCdf: GeckoComponentDataFrame
  comparisonCdf: GeckoComponentDataFrame
  featureRectangles: readonly ShortTermCorrosionFeatureRectangle[]
  params?: ShortTermCorrosionRateGenerationParams
}

export type CalculateShortTermCorrosionRateCdfProps =
  GenerateShortTermCorrosionRateCdfProps & {
    featureRectangles: readonly ShortTermCorrosionFeatureRectangle[]
    priorSupports: readonly ShortTermCorrosionPriorSupport[]
  }

export type CalculateShortTermRemainingLifeCdfProps = {
  targetCdf: GeckoComponentDataFrame
  targetInspection: GeckoInspection
  comparisonCdf: GeckoComponentDataFrame
  comparisonInspection: GeckoInspection
  shortTermRateCdf: GeckoComponentDataFrame
  assetModel: GeckoAssetModel
  componentId: string
  forecastComponentConfig: ForecastComponentConfig | undefined
  forecastComponentConfigGeneration?: number
  tminSource: TminSource
}

export enum ShortTermTminSource {
  Recommended = 'recommended',
  ManualOverride = 'manual_override',
}

export type GeneratedShortTermCorrosionRateCdfResult = {
  resultCdf: GeckoComponentDataFrame
  binnedDataBuffer: GeckoBinnedDataBuffer
  summary: ShortTermFeatureRateSummary
  featureRectangles?: readonly ShortTermCorrosionFeatureRectangle[]
  priorSupports?: readonly ShortTermCorrosionPriorSupport[]
}

export type GeneratedShortTermRemainingLifeCdfResult = {
  resultCdf: GeckoComponentDataFrame
  binnedDataBuffer: GeckoBinnedDataBuffer
  summary: ShortTermRemainingLifeSummary
  sourceRateCdf: GeckoComponentDataFrame
  tminSource: ShortTermTminSource
  manualTminOverride?: GeckoMeasurement
}

export type GeneratedShortTermCorrosionWorkflowCdfResult =
  | GeneratedShortTermCorrosionRateCdfResult
  | GeneratedShortTermRemainingLifeCdfResult

export const DEFAULT_SHORT_TERM_CORROSION_RATE_GENERATION_PARAMS: ShortTermCorrosionRateGenerationParams =
  {
    target_feature_core_dfn_percent: TARGET_FEATURE_CORE_DFN_PERCENT,
    target_feature_grow_dfn_percent: TARGET_FEATURE_GROW_DFN_PERCENT,
    min_feature_area_feet_squared: TARGET_FEATURE_MIN_AREA_FEET_SQUARED,
    target_feature_merge_distance_feet: TARGET_FEATURE_MERGE_DISTANCE_FEET,
    target_feature_max_rect_area_feet_squared:
      TARGET_FEATURE_MAX_RECT_AREA_FEET_SQUARED,
    target_feature_rect_padding_feet: TARGET_FEATURE_RECT_PADDING_FEET,
    prior_support_search_radius_feet: PRIOR_SUPPORT_SEARCH_RADIUS_FEET,
    prior_support_translation_step_bins: PRIOR_SUPPORT_TRANSLATION_STEP_BINS,
    prior_support_distance_weight: PRIOR_SUPPORT_DISTANCE_WEIGHT,
    prior_support_min_coverage_fraction: PRIOR_SUPPORT_MIN_COVERAGE_FRACTION,
    prior_support_missing_coverage_weight:
      PRIOR_SUPPORT_MISSING_COVERAGE_WEIGHT,
    prior_support_min_value_count: PRIOR_SUPPORT_MIN_VALUE_COUNT,
  }

export function useDetectShortTermCorrosionFeatureRectangles() {
  const fetchCdfValues = useGetCdfFlatDataFetcher()

  return useCallback(
    async ({
      targetCdf,
      targetDifferenceFromNominalCdf,
      observationRectangles = [],
      params = DEFAULT_SHORT_TERM_CORROSION_RATE_GENERATION_PARAMS,
    }: DetectShortTermCorrosionFeatureRectanglesProps): Promise<DetectShortTermFeatureRectanglesResult> => {
      const [targetThicknessValues, targetDifferenceFromNominalValues] =
        await Promise.all([
          fetchCdfValues(targetCdf),
          fetchCdfValues(targetDifferenceFromNominalCdf),
        ])

      if (!targetThicknessValues || !targetDifferenceFromNominalValues) {
        throw new Error('Failed to load short-term corrosion detection data.')
      }

      return runWebWorker<DetectShortTermFeatureRectanglesResult>(
        new ShortTermRateWorker(),
        {
          operation: 'detectFeatureRectangles',
          targetThicknessValues,
          targetDifferenceFromNominalValues,
          targetGrid: getFeatureGridMetadata(targetCdf),
          observationRectangles,
          targetFeatureCoreDfnPercent: params.target_feature_core_dfn_percent,
          targetFeatureGrowDfnPercent: params.target_feature_grow_dfn_percent,
          minFeatureAreaFeetSquared: params.min_feature_area_feet_squared,
          targetFeatureMergeDistanceFeet:
            params.target_feature_merge_distance_feet,
          targetFeatureMaxRectAreaFeetSquared:
            params.target_feature_max_rect_area_feet_squared,
        },
        getTransferableGridValueBuffers(
          targetThicknessValues,
          targetDifferenceFromNominalValues
        )
      )
    },
    [fetchCdfValues]
  )
}

export function useMatchShortTermCorrosionFeatureRectangles() {
  const fetchCdfValues = useGetCdfFlatDataFetcher()

  return useCallback(
    async ({
      targetCdf,
      comparisonCdf,
      featureRectangles,
      params = DEFAULT_SHORT_TERM_CORROSION_RATE_GENERATION_PARAMS,
    }: MatchShortTermCorrosionFeatureRectanglesProps): Promise<MatchShortTermFeatureRectanglesResult> => {
      const [targetThicknessValues, comparisonThicknessValues] =
        await Promise.all([
          fetchCdfValues(targetCdf),
          fetchCdfValues(comparisonCdf),
        ])

      if (!targetThicknessValues || !comparisonThicknessValues) {
        throw new Error('Failed to load short-term corrosion matching data.')
      }

      return runWebWorker<MatchShortTermFeatureRectanglesResult>(
        new ShortTermRateWorker(),
        {
          operation: 'matchFeatureRectangles',
          featureRectangles,
          targetThicknessValues,
          comparisonThicknessValues,
          targetGrid: getFeatureGridMetadata(targetCdf),
          comparisonGrid: getFeatureGridMetadata(comparisonCdf),
          targetFeatureRectPaddingFeet: params.target_feature_rect_padding_feet,
          priorSupportSearchRadiusFeet: params.prior_support_search_radius_feet,
          priorSupportTranslationStepBins:
            params.prior_support_translation_step_bins,
          priorSupportDistanceWeight: params.prior_support_distance_weight,
          priorSupportMinCoverageFraction:
            params.prior_support_min_coverage_fraction,
          priorSupportMissingCoverageWeight:
            params.prior_support_missing_coverage_weight,
          priorSupportMinValueCount: params.prior_support_min_value_count,
        },
        getTransferableGridValueBuffers(
          targetThicknessValues,
          comparisonThicknessValues
        )
      )
    },
    [fetchCdfValues]
  )
}

export function useCalculateShortTermCorrosionRateCdf() {
  const fetchCdfValues = useGetCdfFlatDataFetcher()
  const { putBinnedDataBufferInCache } = useBinnedDataBufferCache()

  return useCallback(
    async ({
      params = DEFAULT_SHORT_TERM_CORROSION_RATE_GENERATION_PARAMS,
      featureRectangles,
      priorSupports,
      ...series
    }: CalculateShortTermCorrosionRateCdfProps): Promise<GeneratedShortTermCorrosionRateCdfResult> => {
      const cdfObject = assembleGeneratedShortTermRateCdf({
        ...series,
        params,
        featureRectangles,
      })

      const [targetThicknessValues, comparisonThicknessValues] =
        await Promise.all([
          fetchCdfValues(series.targetCdf),
          fetchCdfValues(series.comparisonCdf),
        ])

      if (!targetThicknessValues || !comparisonThicknessValues) {
        throw new Error('Failed to load short-term corrosion calculation data.')
      }

      const workerResult =
        await runWebWorker<CalculateShortTermFeatureRectangleRatesResult>(
          new ShortTermRateWorker(),
          {
            operation: 'calculateFeatureRectangleRates',
            featureRectangles,
            priorSupports,
            targetThicknessValues,
            comparisonThicknessValues,
            targetGrid: getFeatureGridMetadata(series.targetCdf),
            comparisonGrid: getFeatureGridMetadata(series.comparisonCdf),
            measurementUnitId: series.targetCdf.data.params.measurement_unit.id,
            targetInspectionDate:
              series.targetInspection.data.date.toISOString(),
            comparisonInspectionDate:
              series.comparisonInspection.data.date.toISOString(),
            priorSupportMinValueCount: params.prior_support_min_value_count,
            includeFeatureDetails: false,
          },
          getTransferableGridValueBuffers(
            targetThicknessValues,
            comparisonThicknessValues
          )
        )
      const dataBuffer = createBinnedDataBufferFromData(
        flatten2DValues(workerResult.rateMmPerYear),
        cdfObject
      )
      const memoizedGetterFn = memoize(async () => dataBuffer)

      putBinnedDataBufferInCache({
        id: cdfObject.id,
        generation: cdfObject.generation,
        data: memoizedGetterFn,
      })

      return {
        resultCdf: cdfObject,
        binnedDataBuffer: dataBuffer,
        summary: workerResult.summary,
        featureRectangles,
        priorSupports,
      }
    },
    [fetchCdfValues, putBinnedDataBufferInCache]
  )
}

export function useCalculateShortTermRemainingLifeCdf() {
  const fetchCdfValues = useGetCdfFlatDataFetcher()
  const { putBinnedDataBufferInCache } = useBinnedDataBufferCache()

  return useCallback(
    async ({
      forecastComponentConfig,
      forecastComponentConfigGeneration,
      ...props
    }: CalculateShortTermRemainingLifeCdfProps): Promise<GeneratedShortTermRemainingLifeCdfResult> => {
      const cdfObject = assembleGeneratedShortTermRemainingLifeCdf({
        ...props,
        forecastComponentConfigGeneration,
      })

      const [targetThicknessValues, shortTermRateValuesMmPerYear] =
        await Promise.all([
          fetchCdfValues(props.targetCdf),
          fetchCdfValues(props.shortTermRateCdf),
        ])

      if (!targetThicknessValues || !shortTermRateValuesMmPerYear) {
        throw new Error('Failed to load short-term remaining life input data.')
      }

      const workerResult =
        await runWebWorker<CalculateShortTermFeatureRemainingLifeResult>(
          new ShortTermRateWorker(),
          {
            operation: 'calculateFeatureRemainingLife',
            componentId: props.componentId,
            assetModelJson: props.assetModel.raw,
            forecastComponentConfig: forecastComponentConfig ?? {},
            tminSource: serializeTminSource(props.tminSource),
            targetThicknessValues,
            shortTermRateValuesMmPerYear,
            targetGrid: getFeatureGridMetadata(props.targetCdf),
            measurementUnitId: props.targetCdf.data.params.measurement_unit.id,
            targetInspectionDate:
              props.targetInspection.data.date.toISOString(),
          },
          getTransferableGridValueBuffers(
            targetThicknessValues,
            shortTermRateValuesMmPerYear
          )
        )
      const dataBuffer = createBinnedDataBufferFromData(
        flatten2DValues(workerResult.remainingLifeYears),
        cdfObject
      )
      const memoizedGetterFn = memoize(async () => dataBuffer)

      putBinnedDataBufferInCache({
        id: cdfObject.id,
        generation: cdfObject.generation,
        data: memoizedGetterFn,
      })

      return {
        resultCdf: cdfObject,
        binnedDataBuffer: dataBuffer,
        summary: workerResult.summary,
        sourceRateCdf: props.shortTermRateCdf,
        tminSource: getShortTermTminSource(props.tminSource),
        manualTminOverride:
          props.tminSource.kind === 'manual'
            ? props.tminSource.value
            : undefined,
      }
    },
    [fetchCdfValues, putBinnedDataBufferInCache]
  )
}

export function getGeneratedShortTermRateCdfId(
  series: ShortTermRateGenerationSeries,
  params: ShortTermCorrosionRateGenerationParams = DEFAULT_SHORT_TERM_CORROSION_RATE_GENERATION_PARAMS,
  featureRectangles: readonly ShortTermCorrosionFeatureRectangle[] = []
): string {
  return getGeneratedShortTermRateCdfIdBase(series, params, featureRectangles)
}

export function getGeneratedShortTermRemainingLifeCdfId({
  targetCdf,
  shortTermRateCdf,
  tminSource,
  forecastComponentConfigGeneration,
}: {
  targetCdf: GeckoComponentDataFrame
  shortTermRateCdf: GeckoComponentDataFrame
  tminSource: TminSource
  forecastComponentConfigGeneration?: number
}): string {
  return getGeneratedShortTermRemainingLifeCdfIdBase({
    targetCdf,
    shortTermRateCdf,
    tminSource,
    forecastComponentConfigGeneration,
  })
}

export function assembleGeneratedShortTermRemainingLifeCdf(
  props: Omit<
    CalculateShortTermRemainingLifeCdfProps,
    'assetModel' | 'componentId' | 'forecastComponentConfig'
  >
) {
  return assembleGeneratedShortTermRemainingLifeCdfBase(props)
}

function useGetCdfFlatDataFetcher() {
  const fetchCdfData = useCdfDataFetcher()

  return useCallback(
    async (cdf: GeckoComponentDataFrame): Promise<Float32Array | undefined> => {
      const buffer = await fetchCdfData(cdf)
      if (!buffer) return undefined

      return new Float32Array(buffer.values)
    },
    [fetchCdfData]
  )
}
